import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    CreateFormFeatureDialog,
    HelixLoaderComponent,
} from '../../../../components';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { checkForInvalidProperties } from '../../../../components/paired-input-selection-widget/paired-input-selection-helper';
import { GLOSSARY_METADATA_PATHS } from '../../../../constants/glossary-metadata-paths';
import { useAuth, usePermission } from '../../../../core';
import {
    useAddGlossaryMetadata,
    useGlossaryMetadataList,
} from '../../../../hooks';
import {
    possiblyNavigateToMetadataSearch,
    validationsMessages,
} from '../../../../lib';
import { AttributeAssociationItem } from '../../../edge-definitions/edge-definition-helper';
import {
    checkForCaseSensitiveDuplicates,
    checkForDuplicatedTerm,
    defaultGlossary,
    GlossaryMetadataItem,
    glossaryMetadataSchema,
    glossaryValidationMessages,
    possiblySetDuplicateErrors,
} from '../../glossary-metadata-helper';
import { GlossaryMetadataForm } from '../../GlossaryMetadataForm';
import { glossaryMetadataConfig } from './create-glossary-metadata-helper';

export const CreateGlossaryMetadata = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hasPermissions } = usePermission();

    const [aliases, setAliases] = useState<string[]>([]);
    const [abbreviations, setAbbreviations] = useState<string[]>([]);
    const [glossaryAssociationList, setGlossaryAssociationList] = useState<
        AttributeAssociationItem[]
    >([]);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);

    const { mutate: saveGlossaryMetadata } = useAddGlossaryMetadata();
    const { data: glossaryMetadataList, isLoading } = useGlossaryMetadataList();

    const {
        formState: { errors },
        watch,
        control,
        setError,
        clearErrors,
    } = useForm<GlossaryMetadataItem>({
        mode: 'all',
        defaultValues: defaultGlossary,
        resolver: yupResolver(glossaryMetadataSchema),
    });

    const [term, description, groupName, type] = watch([
        'term',
        'description',
        'groupName',
        'type',
    ]);

    useEffect(() => {
        possiblySetDuplicateErrors(
            'abbreviations',
            setError,
            clearErrors,
            abbreviations
        );
    }, [abbreviations]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        possiblySetDuplicateErrors('aliases', setError, clearErrors, aliases);
    }, [aliases]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGlossaryLandingPageNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            `${GLOSSARY_METADATA_PATHS.LANDING_PAGE}?option=create-glossary`,
            searchParams
        );
    };

    const handleGlossaryBackButton = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            `${GLOSSARY_METADATA_PATHS.CREATE_NEW_GLOSSARY_METADATA_VIEW}?option=create-glossary`,
            searchParams
        );
    };

    const isValidCustomValidation = (): boolean => {
        const isTermDuplicated = checkForDuplicatedTerm(
            glossaryMetadataList?.data ?? [],
            term
        );
        const areAbbreviationsDuplicated =
            checkForCaseSensitiveDuplicates(abbreviations);
        const areAliasesDuplicated = checkForCaseSensitiveDuplicates(aliases);
        const { isListValid, updatedList } = checkForInvalidProperties(
            glossaryAssociationList
        );
        const isTermError = !!errors.term;
        const isDescriptionError = !!errors.description;
        const isGroupNameError = !!errors.groupName;
        const isTypeError = !!errors.type;

        if (!term) {
            setError('term', {
                message: validationsMessages.required,
            });
        }
        if (isTermDuplicated) {
            setError('term', {
                message: glossaryValidationMessages.duplicatedTerm,
            });
        }
        if (areAbbreviationsDuplicated) {
            setError('abbreviations', {
                message: glossaryValidationMessages.duplicatedOptions,
            });
        }
        if (areAliasesDuplicated) {
            setError('aliases', {
                message: glossaryValidationMessages.duplicatedOptions,
            });
        }

        setGlossaryAssociationList(updatedList);

        return (
            !!term &&
            !isTermDuplicated &&
            !areAbbreviationsDuplicated &&
            !areAliasesDuplicated &&
            isListValid &&
            !isTermError &&
            !isDescriptionError &&
            !isGroupNameError &&
            !isTypeError
        );
    };

    const possiblySaveGlossaryMetadataForm = (): void => {
        const permission = hasPermissions('createMetadata');
        if (permission) {
            const newGlossaryItem: GlossaryMetadataItem = {
                term: term,
                description: description,
                groupName: groupName,
                type: type,
                aliases: aliases,
                abbreviations: abbreviations,
                properties: glossaryAssociationList.reduce(
                    (acc: any, item: AttributeAssociationItem) => {
                        acc[item.attributeAssociation.leftSideValue] =
                            item.attributeAssociation.rightSideValue;
                        return acc;
                    },
                    {}
                ),
            };

            saveGlossaryMetadata(
                { glossary: newGlossaryItem, token: getToken() },
                {
                    onSuccess: () => {
                        setTimeout(() => {
                            handleGlossaryLandingPageNavigation();
                        }, 500);
                    },
                }
            );
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const onSubmit = (): void => {
        if (isValidCustomValidation()) {
            possiblySaveGlossaryMetadataForm();
        }
    };

    const handleDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handlePropertyListChanged = (
        newList: AttributeAssociationItem[]
    ): void => {
        setGlossaryAssociationList(newList);
    };

    const handleAbbreviationsChanged = (value: string[]): void => {
        setAbbreviations(value);
    };

    const handleAliasesChanged = (value: string[]): void => {
        setAliases(value);
    };

    if (isLoading) {
        return <HelixLoaderComponent />;
    }

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            handleSubmit={onSubmit}
            handleCloseButton={handleGlossaryLandingPageNavigation}
            handleBackButton={handleGlossaryBackButton}
            listConfig={glossaryMetadataConfig}
        >
            <GlossaryMetadataForm
                onAliasesChanged={handleAliasesChanged}
                onAbbreviationsChanged={handleAbbreviationsChanged}
                onPropertiesListChanged={handlePropertyListChanged}
                control={control}
                errors={errors}
                aliases={aliases}
                abbreviations={abbreviations}
                glossaryAssociationList={glossaryAssociationList}
            />
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handleDialogClose}
                />
            )}
        </CreateFormFeatureDialog>
    );
};
